import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ErthNav } from "../erth/ErthNav";
import { ErthFooter } from "../erth/ErthFooter";
import { Reveal } from "../components/Reveal";
import { ArticleCard, relatedTo } from "../journal/ArticleCard";
import { AUTHOR, getArticle, readingTime, formatDate } from "../journal/articles";
import { useSeo } from "../hooks/useSeo";
import { ORGANIZATION, breadcrumbs, faqSchema, graph } from "../seo/schema";

const Section = ({ s }) => (
  <section className="mt-12">
    <h2 className="font-head text-2xl font-semibold leading-snug text-erth-black sm:text-3xl">{s.h}</h2>
    {s.p?.map((t, i) => (
      <p key={i} className="mt-5 font-body text-lg font-light leading-relaxed text-erth-black/80">{t}</p>
    ))}
    {s.list && (
      <ul className="mt-5 space-y-3">
        {s.list.map((t) => (
          <li key={t} className="flex gap-4 font-body text-lg font-light leading-relaxed text-erth-black/80">
            <span className="mt-[14px] h-1.5 w-1.5 shrink-0 rounded-full bg-erth-black" aria-hidden="true" />
            {t}
          </li>
        ))}
      </ul>
    )}
  </section>
);

const Article = ({ a }) => {
  useSeo({
    title: `${a.title} | erth. escapes`,
    description: a.description,
    path: `/journal/${a.slug}`,
    image: a.cover,
    type: "article",
    jsonLd: graph(
      ORGANIZATION,
      breadcrumbs([{ name: "erth. escapes", path: "/" }, { name: "Journal", path: "/journal" }, { name: a.title, path: `/journal/${a.slug}` }]),
      {
        "@type": "Article",
        headline: a.title,
        description: a.description,
        image: `__ORIGIN__${a.cover}`,
        datePublished: a.date,
        dateModified: a.updated || a.date,
        keywords: a.keywords.join(", "),
        author: { "@type": "Organization", name: AUTHOR.name, url: "__ORIGIN__/" },
        publisher: { "@id": "__ORIGIN__/#organization" },
        mainEntityOfPage: `__ORIGIN__/journal/${a.slug}`,
        ...(a.property ? { about: { "@id": "__ORIGIN__/dune-villas#lodging" } } : {}),
      },
      faqSchema(a.faqs)
    ),
  });

  return (
    <article data-testid="article-page" className="mx-auto max-w-7xl px-6 pb-20 pt-14 sm:px-8 lg:pb-32 lg:pt-20">
      <div className="mx-auto max-w-3xl">
        <nav aria-label="Breadcrumb" data-testid="article-breadcrumb" className="mb-6 font-body text-xs tracking-[0.22em] text-erth-black/50">
          <Link to="/" className="hover:opacity-60">erth. escapes</Link> / <Link to="/journal" className="hover:opacity-60">journal</Link>
        </nav>
        <h1 data-testid="article-title" className="font-head text-3xl font-semibold leading-[1.15] tracking-tight text-erth-black sm:text-4xl lg:text-5xl">
          {a.title}
        </h1>
        <p data-testid="article-meta" className="mt-6 font-body text-sm tracking-[0.12em] text-erth-black/55">
          {AUTHOR.name} · {formatDate(a.date)} · {readingTime(a)} min read
        </p>
      </div>

      <div className="mt-12 aspect-[21/9] overflow-hidden">
        <img src={a.cover} alt={a.coverAlt} fetchpriority="high" className="h-full w-full object-cover" />
      </div>

      <div className="mx-auto mt-14 max-w-3xl">
        <aside data-testid="article-quick-answer" className="border-l-2 border-erth-black pl-6">
          <p className="font-head text-xs font-semibold lowercase tracking-[0.25em] text-erth-black/60">in short</p>
          <p className="mt-3 font-body text-lg leading-relaxed text-erth-black sm:text-xl">{a.quickAnswer}</p>
        </aside>

        {a.table && (
          <dl data-testid="article-key-facts" className="mt-12 grid gap-y-4 border-t border-erth-black/15 pt-8 sm:grid-cols-[minmax(0,180px)_1fr] sm:gap-x-8">
            {a.table.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-head text-sm font-semibold text-erth-black">{k}</dt>
                <dd className="font-body text-base font-light leading-relaxed text-erth-black/80 sm:border-b sm:border-erth-black/10 sm:pb-4">{v}</dd>
              </div>
            ))}
          </dl>
        )}

        {a.sections.map((s) => <Section key={s.h} s={s} />)}

        <section data-testid="article-faqs" className="mt-16 border-t border-erth-black/15 pt-10">
          <h2 className="font-head text-2xl font-semibold text-erth-black sm:text-3xl">Frequently asked questions</h2>
          <dl className="mt-6 divide-y divide-erth-black/10">
            {a.faqs.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-head text-lg font-semibold text-erth-black">{f.q}</dt>
                <dd className="mt-3 font-body text-lg font-light leading-relaxed text-erth-black/80">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-14 flex flex-col gap-4 sm:flex-row">
          {a.property && (
            <Link to="/dune-villas" data-testid="article-property-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-erth-black px-8 py-4 font-head text-[14px] font-semibold lowercase text-erth-white transition-colors hover:bg-erth-black/80">
              view dune villas <ArrowUpRight size={16} strokeWidth={2} />
            </Link>
          )}
          <Link to="/#contact" data-testid="article-enquire-cta" className="inline-flex items-center justify-center rounded-full border border-erth-black px-8 py-4 font-head text-[14px] font-semibold lowercase text-erth-black transition-colors hover:bg-erth-black hover:text-erth-white">
            enquire
          </Link>
        </div>
      </div>

      <div className="mx-auto mt-24 max-w-7xl border-t border-erth-black/15 pt-14">
        <p className="font-head text-xs font-semibold lowercase tracking-[0.25em] text-erth-black/60">keep reading</p>
        <div className="mt-8 grid gap-x-8 gap-y-12 sm:grid-cols-2">
          {relatedTo(a).map((r, i) => (
            <Reveal key={r.slug} delay={i * 0.08}><ArticleCard a={r} /></Reveal>
          ))}
        </div>
      </div>
    </article>
  );
};

export default function ArticlePage() {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/journal" replace />;

  return (
    <div className="min-h-screen bg-erth-beige font-body">
      <ErthNav />
      <main className="pt-[4.5rem]">
        <Article key={a.slug} a={a} />
      </main>
      <ErthFooter />
    </div>
  );
}
